'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var uuid = require('uuid');
var castArray = require('lodash/castArray');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var castArray__default = /*#__PURE__*/_interopDefaultLegacy(castArray);

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const KEY_CURRENT_USER = 'current_user';
class UserReference {
    constructor(app, id) {
        this._ref = app.database().class('_User').object(id);
    }
    get app() {
        return this._ref.app;
    }
    get className() {
        return this._ref.className;
    }
    get id() {
        return this._ref.id;
    }
    refreshSessionToken(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.app.config.masterKey) {
                throw new Error('The masterKey is required when refresh user sessionToken by reference');
            }
            const { sessionToken } = yield this.app.request({
                method: 'PUT',
                path: `/1.1/users/${this.id}/refreshSessionToken`,
            }, Object.assign(Object.assign({}, options), { useMasterKey: true, sessionToken: undefined }));
            return sessionToken;
        });
    }
    dissociateAuthData(platform, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const db = this.app.database();
            yield this.update({ [`authData.${platform}`]: db.op.unset() }, options);
        });
    }
    update(data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.app.config.masterKey) {
                throw new Error('The masterKey is required when update user by reference');
            }
            yield this._ref.update(data, Object.assign(Object.assign({}, options), { useMasterKey: true, sessionToken: undefined }));
        });
    }
    _LC_encode() {
        return this._ref._LC_encode();
    }
    toJSON() {
        return this._LC_encode();
    }
}
class User {
    constructor(_object) {
        this._object = _object;
        this._isLCObject = true;
    }
    get app() {
        return this._object.app;
    }
    get className() {
        return this._object.className;
    }
    get id() {
        return this._object.id;
    }
    get data() {
        return this._object.data;
    }
    get username() {
        return this.data.username;
    }
    get email() {
        return this.data.email;
    }
    get emailVerified() {
        return this.data.emailVerified;
    }
    get mobilePhoneNumber() {
        return this.data.mobilePhoneNumber;
    }
    get mobilePhoneVerified() {
        return this.data.mobilePhoneVerified;
    }
    get sessionToken() {
        return this.data.sessionToken;
    }
    get authData() {
        return this.data.authData;
    }
    get createdAt() {
        return this.data.createdAt;
    }
    get updatedAt() {
        return this.data.updatedAt;
    }
    static fromJSON(app, json) {
        return new User(app.database().decodeObject(json, '_User'));
    }
    static getCurrent(app, cachedOnly = false) {
        if (KEY_CURRENT_USER in app.payload) {
            return app.payload[KEY_CURRENT_USER];
        }
        if (cachedOnly) {
            return null;
        }
        const user_str = app.localStorage.get(KEY_CURRENT_USER);
        app.payload[KEY_CURRENT_USER] = user_str ? User.fromJSON(app, JSON.parse(user_str)) : null;
        return app.payload[KEY_CURRENT_USER];
    }
    static getCurrentAsync(app) {
        return __awaiter(this, void 0, void 0, function* () {
            if (KEY_CURRENT_USER in app.payload) {
                return app.payload[KEY_CURRENT_USER];
            }
            const user_str = yield app.localStorage.getAsync(KEY_CURRENT_USER);
            app.payload[KEY_CURRENT_USER] = user_str ? User.fromJSON(app, JSON.parse(user_str)) : null;
            return yield app.payload[KEY_CURRENT_USER];
        });
    }
    static setCurrentAsync(user) {
        return __awaiter(this, void 0, void 0, function* () {
            user.app.payload[KEY_CURRENT_USER] = user;
            yield user.app.localStorage.setAsync(KEY_CURRENT_USER, JSON.stringify(user._object._LC_encode()));
        });
    }
    static removeCurrent(app) {
        delete app.payload[KEY_CURRENT_USER];
        app.localStorage.remove(KEY_CURRENT_USER);
    }
    static removeCurrentAsync(app) {
        return __awaiter(this, void 0, void 0, function* () {
            delete app.payload[KEY_CURRENT_USER];
            yield app.localStorage.removeAsync(KEY_CURRENT_USER);
        });
    }
    isCurrent() {
        return this === this.app.payload[KEY_CURRENT_USER];
    }
    isAnonymous() {
        var _a;
        return Boolean((_a = this.authData) === null || _a === void 0 ? void 0 : _a.anonymous);
    }
    isAuthenticated() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.app.request({
                    method: 'GET',
                    path: '/1.1/users/me',
                }, { sessionToken: this.sessionToken });
                return true;
            }
            catch (error) {
                if (error.code === 211) {
                    return false;
                }
                throw error;
            }
        });
    }
    updatePassword(oldPassword, newPassword, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionToken, updatedAt } = (yield this.app.request({
                method: 'PUT',
                path: `/1.1/users/${this.id}/updatePassword`,
                body: {
                    old_password: oldPassword,
                    new_password: newPassword,
                },
            }, Object.assign(Object.assign({}, options), { sessionToken: this.sessionToken })));
            this._object._rawData.sessionToken = sessionToken;
            this._object._rawData.updatedAt = updatedAt;
            this.data.sessionToken = sessionToken;
            this.data.updatedAt = new Date(updatedAt);
        });
    }
    refreshSessionToken(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { sessionToken, updatedAt } = yield this.app.request({
                method: 'PUT',
                path: `/1.1/users/${this.id}/refreshSessionToken`,
            }, Object.assign(Object.assign({}, options), { sessionToken: this.sessionToken }));
            this._object._rawData.sessionToken = sessionToken;
            this._object._rawData.updatedAt = updatedAt;
            this.data.sessionToken = sessionToken;
            this.data.updatedAt = new Date(updatedAt);
            return sessionToken;
        });
    }
    associateWithAuthData(platform, authDataItem, options) {
        return this.update({ authData: { [platform]: authDataItem } }, options);
    }
    associateWithAuthDataAndUnionId(platform, authDataItem, unionId, options) {
        return this.associateWithAuthData(platform, Object.assign(Object.assign({}, authDataItem), { unionid: unionId, platform: (options === null || options === void 0 ? void 0 : options.unionIdPlatform) || 'weixin', main_account: options === null || options === void 0 ? void 0 : options.asMainAccount }), options);
    }
    get(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const obj = yield this._object.get(Object.assign(Object.assign({}, options), { sessionToken: this.sessionToken }));
            this._merge(obj);
            if (this.isCurrent()) {
                yield User.setCurrentAsync(this);
            }
            return this;
        });
    }
    update(data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const obj = yield this._object.update(data, Object.assign(Object.assign({}, options), { fetchUpdatedData: true, sessionToken: this.sessionToken }));
            this._merge(obj);
            if (this.isCurrent()) {
                yield User.setCurrentAsync(this);
            }
            return this;
        });
    }
    delete(options) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._object.delete(Object.assign(Object.assign({}, options), { sessionToken: this.sessionToken }));
            if (this.isCurrent()) {
                yield this.app.localStorage.removeAsync(KEY_CURRENT_USER);
            }
        });
    }
    toJSON() {
        return this._object.toJSON();
    }
    _merge(obj) {
        Object.assign(this._object._rawData, obj._rawData);
        Object.assign(this.data, obj.data);
    }
    _LC_encode(options) {
        return this._object._LC_encode(options);
    }
}

class RoleReference {
    constructor(app, id) {
        this._isLCObjectRef = true;
        this._ref = app.database().class('_Role', Role.fromJSON).object(id);
    }
    get app() {
        return this._ref.app;
    }
    get className() {
        return this._ref.className;
    }
    get id() {
        return this._ref.id;
    }
    add(subjects, options) {
        const users = [];
        const roles = [];
        castArray__default["default"](subjects).forEach((subject) => {
            switch (subject.className) {
                case '_User':
                    users.push(subject);
                    break;
                case '_Role':
                    roles.push(subject);
                    break;
                default:
                    throw new TypeError('Can only add a user or another role to a role');
            }
        });
        const { addRelation } = this.app.database().op;
        return this.update({
            users: users.length ? addRelation(users) : undefined,
            roles: roles.length ? addRelation(roles) : undefined,
        }, options);
    }
    remove(subjects, options) {
        const users = [];
        const roles = [];
        castArray__default["default"](subjects).forEach((subject) => {
            switch (subject.className) {
                case '_User':
                    users.push(subject);
                    break;
                case '_Role':
                    roles.push(subject);
                    break;
                default:
                    throw new TypeError('Can only remove a user or another role from a role');
            }
        });
        const { removeRelation } = this.app.database().op;
        return this.update({
            users: users.length ? removeRelation(users) : undefined,
            roles: roles.length ? removeRelation(roles) : undefined,
        }, options);
    }
    queryUser() {
        return this.app
            .database()
            .class('_User', User.fromJSON)
            .where({
            $relatedTo: {
                key: 'users',
                object: this,
            },
        });
    }
    queryRole() {
        return this.app
            .database()
            .class('_Role', Role.fromJSON)
            .where({
            $relatedTo: {
                key: 'roles',
                object: this,
            },
        });
    }
    getUsers(options) {
        return this.queryUser().find(options);
    }
    getRoles(options) {
        return this.queryRole().find(options);
    }
    get(options) {
        return this._ref.get(options);
    }
    update(data, options) {
        return this._ref.update(data, options);
    }
    delete(options) {
        return this._ref.delete(options);
    }
    toJSON() {
        return this._ref.toJSON();
    }
    _LC_encode() {
        return this.toJSON();
    }
}
class Role {
    constructor() {
        this._isLCObject = true;
    }
    get app() {
        return this._object.app;
    }
    get className() {
        return this._object.className;
    }
    get id() {
        return this._object.id;
    }
    get data() {
        return this._object.data;
    }
    get name() {
        return this.data.name;
    }
    get ACLKey() {
        return `role:${this.name}`;
    }
    static fromJSON(app, data) {
        const role = new Role();
        role._object = app.database().decodeObject(data, '_Role');
        role._ref = new RoleReference(app, role.id);
        return role;
    }
    add(subject, options) {
        return this._ref.add(subject, options);
    }
    remove(subject, options) {
        return this._ref.remove(subject, options);
    }
    queryUser() {
        return this._ref.queryUser();
    }
    queryRole() {
        return this._ref.queryRole();
    }
    getUsers(options) {
        return this._ref.getUsers(options);
    }
    getRoles(options) {
        return this._ref.getRoles(options);
    }
    get(options) {
        return this._ref.get(options);
    }
    update(data, options) {
        return this._ref.update(data, options);
    }
    delete(options) {
        return this._ref.delete(options);
    }
    toJSON() {
        return this._object.toJSON();
    }
    _LC_encode(options) {
        const encoded = this._object._LC_encode(options);
        delete encoded.users;
        delete encoded.roles;
        return encoded;
    }
}

let SDKRuntime;
function setSDKRuntime(runtime) {
    SDKRuntime = runtime;
}

function setHooks(appClass) {
    appClass.beforeInvokeAPI((app, request, options) => __awaiter(this, void 0, void 0, function* () {
        if (options.sessionToken) {
            return;
        }
        const user = yield User.getCurrentAsync(app);
        if (user) {
            options.sessionToken = user.sessionToken;
        }
    }));
}
class Auth {
    constructor(app) {
        this.app = app;
    }
    get currentUser() {
        return this.getCurrentUser();
    }
    user(id) {
        return new UserReference(this.app, id);
    }
    role(id) {
        return new RoleReference(this.app, id);
    }
    addRole(data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { ACL, name, users, roles } = data;
            if (!data.ACL || !data.name) {
                throw new Error('The role name and ACL is required');
            }
            const db = this.app.database();
            const rawData = yield this.app.request({
                method: 'POST',
                path: '/1.1/roles',
                query: {
                    fetchWhenSave: options === null || options === void 0 ? void 0 : options.fetchData,
                },
                body: db.encodeObjectData(Object.assign(Object.assign({}, data), { name,
                    ACL, users: users ? db.op.addRelation(users) : undefined, roles: roles ? db.op.addRelation(roles) : undefined })),
            }, options);
            return Role.fromJSON(this.app, rawData);
        });
    }
    queryUser() {
        return this.app.database().class('_User', User.fromJSON);
    }
    queryRole() {
        return this.app.database().class('_Role', Role.fromJSON);
    }
    getCurrentUser(cachedOnly = false) {
        return User.getCurrent(this.app, cachedOnly);
    }
    getCurrentUserAsync() {
        return User.getCurrentAsync(this.app);
    }
    signUp(data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._decodeAndSetCurrent(yield this.app.request({
                method: 'POST',
                path: '/1.1/users',
                body: this.app.database().encodeObjectData(data),
            }, options));
        });
    }
    signUpOrLoginWithMobilePhone(data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._decodeAndSetCurrent(yield this.app.request({
                method: 'POST',
                path: `/1.1/usersByMobilePhone`,
                body: this.app.database().encodeObjectData(data),
            }, options));
        });
    }
    login(arg1, arg2, arg3) {
        return __awaiter(this, void 0, void 0, function* () {
            let body;
            let options;
            if (typeof arg1 === 'string') {
                if (typeof arg2 !== 'string') {
                    throw new Error('参数 password 必须是 string');
                }
                body = {
                    username: arg1,
                    password: arg2,
                };
                options = arg3;
            }
            else {
                body = arg1;
                options = arg2;
            }
            return this._decodeAndSetCurrent(yield this.app.request({
                method: 'POST',
                path: '/1.1/login',
                body,
            }, options));
        });
    }
    loginWithSessionToken(sessionToken) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._decodeAndSetCurrent(yield this.app.request({
                method: 'GET',
                path: '/1.1/users/me',
            }, {
                sessionToken,
            }));
        });
    }
    loginAnonymously(options) {
        const authData = {
            id: uuid.v4(),
        };
        return this.loginWithAuthData('anonymous', authData, options);
    }
    loginWithAuthData(platform, authDataItem, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._decodeAndSetCurrent(yield this.app.request({
                method: 'POST',
                path: '/1.1/users',
                query: {
                    failOnNotExist: options === null || options === void 0 ? void 0 : options.failOnNotExist,
                },
                body: {
                    authData: {
                        [platform]: authDataItem,
                    },
                },
            }, options));
        });
    }
    loginWithAuthDataAndUnionId(platform, authDataItem, unionId, options) {
        return this.loginWithAuthData(platform, Object.assign(Object.assign({}, authDataItem), { unionid: unionId, platform: (options === null || options === void 0 ? void 0 : options.unionIdPlatform) || 'weixin', main_account: options === null || options === void 0 ? void 0 : options.asMainAccount }), options);
    }
    loginWithMiniApp(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const getAuthInfo = SDKRuntime.getAdapter('getAuthInfo');
            const { provider, authData } = yield getAuthInfo(options);
            return this.loginWithAuthData(provider, authData, options);
        });
    }
    loginWithMiniAppAndUnionId(unionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const getAuthInfo = SDKRuntime.getAdapter('getAuthInfo');
            const { provider, authData, platform } = yield getAuthInfo(options);
            return this.loginWithAuthDataAndUnionId(provider, authData, unionId, Object.assign(Object.assign({}, options), { unionIdPlatform: platform }));
        });
    }
    logOut() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Promise.all(Auth.hooks.beforeLogOut.map((h) => h.call(this)));
            yield User.removeCurrentAsync(this.app);
        });
    }
    requestEmailVerify(email, options) {
        return this.app.request({
            method: 'POST',
            path: '/1.1/requestEmailVerify',
            body: { email },
        }, options);
    }
    requestLoginSMSCode(mobilePhoneNumber, options) {
        return this.app.request({
            method: 'POST',
            path: '/1.1/requestLoginSmsCode',
            body: {
                mobilePhoneNumber,
                validate_token: options === null || options === void 0 ? void 0 : options.validateToken,
            },
        }, options);
    }
    requestMobilePhoneVerify(mobilePhoneNumber, options) {
        return this.app.request({
            method: 'POST',
            path: '/1.1/requestMobilePhoneVerify',
            body: {
                mobilePhoneNumber,
                validate_token: options === null || options === void 0 ? void 0 : options.validateToken,
            },
        }, options);
    }
    requestPasswordReset(email, options) {
        return this.app.request({
            method: 'POST',
            path: '/1.1/requestPasswordReset',
            body: { email },
        }, options);
    }
    requestPasswordResetBySMSCode(mobilePhoneNumber, options) {
        return this.app.request({
            method: 'POST',
            path: '/1.1/requestPasswordResetBySmsCode',
            body: {
                mobilePhoneNumber,
                validate_token: options === null || options === void 0 ? void 0 : options.validateToken,
            },
        }, options);
    }
    resetPasswordBySMSCode(smsCode, password, options) {
        return this.app.request({
            method: 'PUT',
            path: `/1.1/resetPasswordBySmsCode/${smsCode}`,
            body: { password },
        }, options);
    }
    verifyMobilePhone(smsCode, options) {
        return this.app.request({
            method: 'POST',
            path: `/1.1/verifyMobilePhone/${smsCode}`,
        }, options);
    }
    requestChangePhoneNumber(mobilePhoneNumber, optoins) {
        return this.app.request({
            method: 'POST',
            path: '/requestChangePhoneNumber',
            body: {
                mobilePhoneNumber,
                ttl: optoins === null || optoins === void 0 ? void 0 : optoins.ttl,
            },
        }, optoins);
    }
    changePhoneNumber(mobilePhoneNumber, smsCode, options) {
        return this.app.request({
            method: 'POST',
            path: '/1.1/changePhoneNumber',
            body: {
                mobilePhoneNumber,
                code: smsCode,
            },
        }, options);
    }
    _decodeAndSetCurrent(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const user = User.fromJSON(this.app, data);
            yield User.setCurrentAsync(user);
            yield Promise.all(Auth.hooks.afterLogin.map((h) => h.call(this)));
            return user;
        });
    }
    static afterLogin(h) {
        Auth.hooks.afterLogin.push(h);
    }
    static beforeLogOut(h) {
        Auth.hooks.beforeLogOut.push(h);
    }
}
Auth.hooks = {
    afterLogin: [],
    beforeLogOut: [],
};

const authModule = {
    name: 'auth',
    components: { Auth, User, Role },
    onLoad: (runtime) => {
        setSDKRuntime(runtime);
        const { App } = runtime.modules.core.components;
        App.prototype.auth = function () {
            return new Auth(this);
        };
        setHooks(App);
    },
};

exports.Auth = Auth;
exports.User = User;
exports.UserReference = UserReference;
exports.authModule = authModule;
